import React from "react";
import { Target } from "lucide-react";
import { CustomRoute } from "@/interfaces";
import type { LeadGenRole } from "@/services/leadGenService";
import LeadGenRoleGuard from "@/modules/Admin/LeadGen/LeadGenRoleGuard";

const Dashboard = React.lazy(() => import("@/modules/Admin/LeadGen/Dashboard/index.tsx"));
const Leads = React.lazy(() => import("@/modules/Admin/LeadGen/Leads/index.tsx"));
const LeadPage = React.lazy(() => import("@/modules/Admin/LeadGen/Leads/LeadPage.tsx"));
const Prospects = React.lazy(() => import("@/modules/Admin/LeadGen/Prospects/index.tsx"));
const Sources = React.lazy(() => import("@/modules/Admin/LeadGen/Sources/index.tsx"));
const Settings = React.lazy(() => import("@/modules/Admin/LeadGen/Settings/index.tsx"));
const Collector = React.lazy(() => import("@/modules/Admin/LeadGen/Collector/index.tsx"));

// every role that works on leads (the Collector role only configures: Lead Sources and Settings)
const ALL: LeadGenRole[] = ["super_admin", "company_admin", "lg_admin", "team_lead", "qualifier", "account_executive", "lg_user"];
const CONFIG: LeadGenRole[] = ["super_admin", "lg_admin", "collector"];
// roles that work the lead list, and those that work prospects and deals
const LEADS: LeadGenRole[] = ["super_admin", "company_admin", "lg_admin", "team_lead", "qualifier", "lg_user"];
const PROSPECTS: LeadGenRole[] = ["super_admin", "company_admin", "lg_admin", "team_lead", "account_executive", "lg_user"];

/**
 * The one CRM section (Lead Gen, merged from LeadPilot, and the CRM module) for the Super Admin, Admin and Employee
 * routers: the lead pipeline first, then the CRM module's pages (`crm`), where a prospect marked Won continues, then
 * Lead Gen's setup. Lead Gen pages go by Lead Gen role (UserMenu filters by leadGenRoles; LeadGenRoleGuard stops typed
 * URLs), CRM pages by CRM access (crmModule); a company with CRM off has no CRM section at all (UserMenu).
 * The collector console is Super Admin only, so only the Super Admin router asks for it.
 */
export function leadGenRoutes(id: string, { collector = false, crm }: { collector?: boolean; crm: CustomRoute[] }): CustomRoute {
  const page = (path: string, menuName: string, roles: LeadGenRole[], element: React.ReactNode, hideInMenu = false): CustomRoute => ({
    path,
    id,
    menuName,
    element: <LeadGenRoleGuard roles={roles}>{element}</LeadGenRoleGuard>,
    leadGenRoles: roles,
    highlightOn: ["lead-gen"],
    hideInMenu,
  });
  return {
    path: "",
    id,
    element: null,
    menuName: "CRM",
    icon: Target,
    highlightOn: ["lead-gen", ...crm.flatMap((r) => r.highlightOn ?? [])],
    submenu: [
      page("/lead-gen/dashboard", "Dashboard", ALL, <Dashboard />),
      page("/lead-gen/leads", "Leads", LEADS, <Leads />),
      page("/lead-gen/leads/:id", "Leads", ALL, <LeadPage />, true),
      page("/lead-gen/prospects", "Prospects", PROSPECTS, <Prospects />),
      // the CRM module's own pages keep their access rules (UserMenu: crmModule)
      ...crm.map((r) => ({ ...r, crmModule: true })),
      page("/lead-gen/sources", "Lead Sources", CONFIG, <Sources />),
      ...(collector ? [page("/lead-gen/collector", "Collector", ["super_admin"], <Collector />)] : []),
      page("/lead-gen/settings", "Settings", CONFIG, <Settings />),
    ],
  };
}
